import { appConfig } from 'src/config';
import {
  EDataType,
  EDataSwitchType,
  type NodeCategoryModel,
} from 'src/modules/network-configuration/models/node-data';

import { parseWebUiPath, type WebUiRouteId } from './routes';

export type SwitchAccessReason = 'not_found' | 'not_managed';

type StoredNode = {
  id: string;
  data?: { category?: NodeCategoryModel };
};

/** Same rule as standard-node dialog: Device or managed Switch. */
export function isWebUiAllowedForCategory(
  category: NodeCategoryModel | undefined,
): boolean {
  if (!category) return false;
  if (category.type === EDataType.Device) return true;
  if (category.type === EDataType.Switch) {
    return Boolean(category.switchType?.includes(EDataSwitchType.MANAGED));
  }
  return false;
}

export function validateSwitchId(
  switchId: string,
): { ok: true } | { ok: false; reason: SwitchAccessReason } {
  try {
    const raw = localStorage.getItem(appConfig.currentStateStorageKey);
    if (!raw) return { ok: false, reason: 'not_found' };
    const state = JSON.parse(raw) as { nodes?: StoredNode[] };
    const { nodes } = state;
    if (!Array.isArray(nodes)) return { ok: false, reason: 'not_found' };
    const node = nodes.find((n) => n.id === switchId);
    if (!node) return { ok: false, reason: 'not_found' };
    if (isWebUiAllowedForCategory(node.data?.category)) return { ok: true };
    return { ok: false, reason: 'not_managed' };
  } catch {
    return { ok: false, reason: 'not_found' };
  }
}

export type WebUiAccessError =
  | { kind: 'missing_switch' }
  | { reason: SwitchAccessReason };

export function getWebUiStateFromPath(pathname: string): {
  switchId: string | null;
  route: WebUiRouteId;
  accessError: WebUiAccessError | null;
} {
  const { switchId: sid, routeId } = parseWebUiPath(pathname);
  if (!sid) {
    return {
      switchId: null,
      route: 'dashboard',
      accessError: { kind: 'missing_switch' },
    };
  }
  const v = validateSwitchId(sid);
  if (!v.ok) {
    return {
      switchId: null,
      route: 'dashboard',
      accessError: { reason: v.reason },
    };
  }
  return { switchId: sid, route: routeId, accessError: null };
}
